import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PLAN_CHANGE_DIRECTION_LABELS,
  PLAN_CHANGE_STATUS_LABELS,
  type PlanChangeRequestForStaff,
} from '@azko/shared';
import { ApiRequestError, api } from '../../lib/api.js';
import { formatCurrency } from '../../lib/format.js';
import { useSession } from '../../lib/session.js';
import {
  Alert,
  Button,
  Card,
  CONTROL_CLASS,
  EmptyState,
  LoadingRows,
  StatusBadge,
  cx,
} from '../../components/ui.js';

/**
 * A fila de trocas de plano que a Azko decide.
 *
 * O que a tela precisa responder antes de qualquer clique: quem pediu, para
 * onde vai e quanto muda no preco. Por isso a direcao e a diferenca vem no
 * topo do card, e nao escondidas atras de um detalhe — decidir sem ver o valor
 * e como o operador aprova um downgrade achando que era upgrade.
 *
 * Recusar abre o campo de motivo, e ele e obrigatorio: o texto vai para a tela
 * do sindico. Recusa sem motivo o deixa sem acao possivel.
 */

interface Queue {
  items: PlanChangeRequestForStaff[];
}

function DirectionBadge({ item }: { item: PlanChangeRequestForStaff }) {
  const delta = item.requestedPriceCents - (item.currentPriceCents ?? 0);

  return (
    <div className="text-right">
      <StatusBadge tone={item.direction === 'upgrade' ? 'accent' : 'neutral'}>
        {PLAN_CHANGE_DIRECTION_LABELS[item.direction]}
      </StatusBadge>
      <p
        className={cx(
          'mt-2 text-sm font-bold tabular-nums',
          delta > 0 ? 'text-accent' : delta < 0 ? 'text-warn' : 'text-muted',
        )}
      >
        {delta > 0 ? '+' : ''}
        {formatCurrency(delta, 'BRL')}/mes
      </p>
    </div>
  );
}

export function PlanChangeRequestsPage() {
  const queryClient = useQueryClient();
  const { staff } = useSession();

  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const podeDecidir = staff?.permissions.includes('plataforma:assinantes:aprovar') ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'solicitacoes'],
    queryFn: () => api.get<Queue>('/admin/solicitacoes'),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'solicitacoes'] });
  }

  function onError(cause: unknown) {
    setError(cause instanceof ApiRequestError ? cause.message : 'Falha ao decidir.');
  }

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/admin/solicitacoes/${id}/aprovar`, {}),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError,
  });

  const reject = useMutation({
    mutationFn: (id: string) =>
      api.post(`/admin/solicitacoes/${id}/recusar`, { reason: reason.trim() }),
    onSuccess: () => {
      setRejecting(null);
      setReason('');
      setError(null);
      refresh();
    },
    onError,
  });

  const items = data?.items ?? [];
  const pendentes = items.filter((item) => item.status === 'pending');
  const decididas = items.filter((item) => item.status !== 'pending');

  return (
    <>
      <div>
        <p className="eyebrow">Assinaturas</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Trocas de plano.</h1>
        <p className="mt-2 text-sm text-muted">
          {pendentes.length === 0
            ? 'Nenhum condominio esperando resposta.'
            : `${pendentes.length} ${pendentes.length === 1 ? 'condominio espera' : 'condominios esperam'} resposta.`}
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      {isLoading && <LoadingRows rows={3} />}

      {!isLoading && items.length === 0 && (
        <EmptyState
          title="Nenhuma solicitacao ainda"
          description="Quando um sindico pedir para trocar de plano, o pedido aparece aqui."
        />
      )}

      {/* Duas colunas no monitor grande: cada pedido e um cartao de decisao
          curto, e um por linha obrigava a rolar a fila inteira para ver o que
          ha nela. `items-start` para o cartao em recusa crescer sozinho. */}
      <div className="grid items-start gap-4 xl:grid-cols-2">
        {pendentes.map((item) => (
          <Card key={item.id} className="border-accent/40 p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl font-bold tracking-tight">{item.condominiumName}</h2>
                <p className="mt-1 text-sm text-muted">
                  {item.currentPlanCode ?? 'sem plano'} → <strong>{item.requestedPlanCode}</strong>
                </p>
                <p className="mt-2 text-sm text-faint">
                  Pedido em{' '}
                  {new Date(item.createdAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                  {item.requestedByName && ` por ${item.requestedByName}`}
                </p>
                {item.note && <p className="mt-3 text-sm text-muted">"{item.note}"</p>}
              </div>

              <DirectionBadge item={item} />
            </div>

            {podeDecidir &&
              (rejecting === item.id ? (
                <div className="mt-5 border-t border-line-soft pt-5">
                  <label
                    className="block text-sm font-semibold text-muted"
                    htmlFor={`reason-${item.id}`}
                  >
                    Motivo da recusa — o sindico vai ler
                  </label>
                  <textarea
                    id={`reason-${item.id}`}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    rows={2}
                    maxLength={255}
                    placeholder="Ex: conta com fatura em aberto desde marco."
                    className={cx(CONTROL_CLASS, 'mt-1.5 resize-none')}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      loading={reject.isPending}
                      disabled={reason.trim().length < 5}
                      onClick={() => reject.mutate(item.id)}
                    >
                      Confirmar recusa
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setRejecting(null);
                        setReason('');
                      }}
                    >
                      Voltar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-5 flex flex-wrap gap-3 border-t border-line-soft pt-5">
                  <Button
                    loading={approve.isPending && approve.variables === item.id}
                    onClick={() => approve.mutate(item.id)}
                  >
                    Aprovar troca
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setRejecting(item.id);
                      setReason('');
                      setError(null);
                    }}
                  >
                    Recusar
                  </Button>
                </div>
              ))}

            {!podeDecidir && (
              <p className="mt-5 border-t border-line-soft pt-5 text-sm text-faint">
                Seu papel ve a fila, mas nao decide. Peca ao time de operacoes.
              </p>
            )}
          </Card>
        ))}
      </div>

      {decididas.length > 0 && (
        <Card className="p-5 sm:p-6">
          <h2 className="text-xl font-bold tracking-tight">Ja respondidas</h2>
          <ul className="mt-5 space-y-4">
            {decididas.map((item) => (
              <li key={item.id} className="border-b border-line-soft pb-4 last:border-0 last:pb-0">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">{item.condominiumName}</span>
                  <span className="text-sm text-muted">
                    {PLAN_CHANGE_STATUS_LABELS[item.status]}
                    {item.decidedByName && ` por ${item.decidedByName}`}
                    {item.decidedAt && ` • ${new Date(item.decidedAt).toLocaleDateString('pt-BR')}`}
                  </span>
                </div>
                <p className="mt-1 text-sm text-faint">
                  {item.currentPlanCode ?? 'sem plano'} → {item.requestedPlanCode}
                  {item.decisionReason && ` • ${item.decisionReason}`}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
